import type { HarmonyPackageDocument } from '@rhythmic/db/types'
import type { UpdateHarmonyPackageDocumentPayload } from '@rhythmic/types'
import { UpdateHarmonyPackageDocumentPayloadSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { defineStore, storeToRefs } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'
import * as z from 'zod'
import { useRhythmicLogger } from '#imports'
import { useReactiveDbPaneScope } from '../../composables/useReactiveDbContextRegistry'
import useHarmonyPlanContext from '../harmonyPlan/useHarmonyPlanContext'
import useTeamContext from '../team/useTeamContext'
import { createMutationHandlers } from './useDocumentContext.mutators'
import useDocumentContextQueries from './useDocumentContext.queries'

export interface DocumentContext {
  teamId: string
  workspaceId: string
  packageId: string
  documentId: string
}

const createDocumentPayloadSchema = z.object({
  name: z.string().min(1, 'Document name is required'),
  image: z.string().nullish(),
  context: z.string().nullish(),
  markdown: z.string().nullish(),
  publish_status: z.enum(['draft', 'published']).optional(),
  rag_summary: z.string().nullish(),
  rag_one_liner: z.string().nullish(),
  folder_id: z.string().nullish(),
})

type CreateDocumentPayload = z.infer<typeof createDocumentPayloadSchema>

export const useDocumentContext = defineStore('documentStore', () => {
  const logger = useRhythmicLogger('documentContextStore')
  const { team } = storeToRefs(useTeamContext())
  const { harmonyPlan, harmonyPlanId } = storeToRefs(useHarmonyPlanContext())
  const pendingSaves = ref(0)
  const pendingLocalEdits = ref(false)

  const packageId = computed<string | undefined>(() => harmonyPlan.value?.id ?? harmonyPlanId.value)

  const queries = useDocumentContextQueries()
  queries.init({ logger, packageId })

  const paneScope = useReactiveDbPaneScope()
  let paneDisposerId: string | null = null

  const context = computed<DocumentContext | undefined>(() => {
    const currentTeam = team.value
    const resolvedPackageId = packageId.value
    const currentDocumentId = queries.documentId.value

    if (!currentTeam?.id || !currentTeam.workspace_id || !resolvedPackageId || !currentDocumentId) return undefined

    return {
      teamId: currentTeam.id,
      workspaceId: currentTeam.workspace_id,
      packageId: resolvedPackageId,
      documentId: currentDocumentId,
    }
  })

  const mutators = createMutationHandlers(context)
  const document = computed(() => queries.document.value)
  const isDirty = computed(() => pendingSaves.value > 0)

  if (paneScope) {
    paneDisposerId = paneScope.registerDisposer(
      async () => {
        queries.clearDocumentScopedQueries()
      },
      { id: 'document-context', label: 'document-context' },
    )
    onScopeDispose(() => {
      if (!paneDisposerId) return
      paneScope.releaseDisposer(paneDisposerId)
      paneDisposerId = null
    })
  }

  function trackTransaction(transaction: Transaction<HarmonyPackageDocument> | null) {
    if (!transaction?.isPersisted?.promise) return
    pendingSaves.value += 1
    void transaction.isPersisted.promise.finally(() => {
      pendingSaves.value = Math.max(0, pendingSaves.value - 1)
    })
  }

  function updateDocument(payload: UpdateHarmonyPackageDocumentPayload): Transaction<HarmonyPackageDocument> | null {
    if (!document.value) {
      return null
    }
    const validated = UpdateHarmonyPackageDocumentPayloadSchema.parse(payload)
    const transaction = mutators.updateDocument(document.value?.id, validated)
    trackTransaction(transaction)
    return transaction
  }

  function setPendingLocalEdits(value: boolean) {
    pendingLocalEdits.value = value
  }

  /**
   * Create a document via the data API using the current plan/workspace context.
   */
  async function createDocument(payload: CreateDocumentPayload) {
    const resolvedPackageId = packageId.value
    const workspaceId = team.value?.workspace_id

    if (!resolvedPackageId) throw new Error('Harmony plan identifier is required to create a document')

    if (!workspaceId) throw new Error('Workspace identifier is required to create a document')

    const validated = createDocumentPayloadSchema.parse(payload)
    const body = {
      ...validated,
      package_id: resolvedPackageId,
      workspace_id: workspaceId,
    }

    logger.debug('Creating document via API', { packageId: resolvedPackageId })

    const response = await $fetch<{ document: HarmonyPackageDocument }>('/api/harmony-package-documents', {
      method: 'POST',
      body,
    })

    return response.document
  }

  /**
   * Patch the current document via the data API.
   */
  async function patchDocument(payload: UpdateHarmonyPackageDocumentPayload) {
    const documentId = document.value?.id ?? queries.documentId.value

    if (!documentId) throw new Error('Document identifier is required to update a document')

    const validated = UpdateHarmonyPackageDocumentPayloadSchema.parse(payload)
    const updates = Object.fromEntries(Object.entries(validated).filter(([, value]) => value !== undefined))

    if (Object.keys(updates).length === 0) throw new Error('At least one field must be provided to update a document')

    logger.debug('Patching document via API', {
      documentId,
      updates: Object.keys(updates),
    })

    pendingSaves.value += 1
    try {
      const response = await $fetch<{ document: HarmonyPackageDocument }>(`/api/document/${documentId}`, {
        method: 'PATCH',
        body: updates,
      })

      return response.document
    } finally {
      pendingSaves.value = Math.max(0, pendingSaves.value - 1)
    }
  }

  return {
    document,
    isDirty,
    pendingLocalEdits,
    setPendingLocalEdits,
    updateDocument,
    createDocument,
    patchDocument,
    queries,
    documentId: queries.documentId,
  }
})
